import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ShieldCheck, ClipboardList, GraduationCap, MessageCircle, Banknote, ScrollText, Users, UserCog, Sparkles, CalendarClock, MessageSquareHeart,
  Repeat, DatabaseBackup, Camera,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { EmSwash } from "../components/EmSwash";
import { useAdminStats } from "../lib/adminQueues";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { CampaignReviewTab } from "../components/admin/CampaignReviewTab";
import { VerificationTab } from "../components/admin/VerificationTab";
import { ModerationTab } from "../components/admin/ModerationTab";
import { FeedbackTab } from "../components/admin/FeedbackTab";
import { DisbursementTab } from "../components/admin/DisbursementTab";
import { AuditLogTab } from "../components/admin/AuditLogTab";
import { WaitlistTab } from "../components/admin/WaitlistTab";
import { SignupsTab } from "../components/admin/SignupsTab";
import { LiveCampaignsTab } from "../components/admin/LiveCampaignsTab";
import { ExtensionsTab } from "../components/admin/ExtensionsTab";
import { SustainingRollPanel } from "../components/admin/SustainingRollPanel";
import { DomainHealthWidget } from "../components/admin/DomainHealthWidget";
import { BackupDownloadButton } from "../components/admin/BackupDownloadButton";
import { BackupStatus } from "../components/admin/BackupStatus";
import { BackupsTab } from "../components/admin/BackupsTab";
import { ConsentStatsWidget } from "../components/admin/ConsentStatsWidget";
import { PaperLightDial } from "../components/admin/PaperLightDial";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * AdminReview — the real Admin console. Four queues under one route:
 *   1. Campaign Review          (real Mongo)
 *   2. Student Verification     (real Mongo)
 *   3. Supporter Wall Moderation (MOCKED seed)
 *   4. Financials / Disbursements (real: Mongo grants + Stripe Connect transfers)
 *
 * Route: /admin  (auth-gated: user.is_admin must be true).
 * Tab state lives in the URL hash so the admin can bookmark straight to
 * "/admin#verification" and refreshes stay on the same tab.
 */
const TABS = [
  { key: "review", label: "Campaign Review", icon: ClipboardList, component: CampaignReviewTab, statKey: "pending_review" },
  { key: "verification", label: "Student Verification", icon: GraduationCap, component: VerificationTab, statKey: "pending_verification" },
  { key: "moderation", label: "Wall Moderation", icon: MessageCircle, component: ModerationTab },
  { key: "feedback", label: "Feedback", icon: MessageSquareHeart, component: FeedbackTab },
  { key: "disbursements", label: "Disbursements", icon: Banknote, component: DisbursementTab },
  { key: "extensions", label: "Extensions & Lifecycle", icon: CalendarClock, component: ExtensionsTab, statKey: "pending_extensions" },
  { key: "campaigns", label: "Live campaigns", icon: Sparkles, component: LiveCampaignsTab, statKey: "live" },
  { key: "signups", label: "Sign-ups & Export", icon: UserCog, component: SignupsTab },
  { key: "waitlist", label: "Waitlist", icon: Users, component: WaitlistTab },
  { key: "sustaining", label: "Sustaining donors", icon: Repeat, component: SustainingRollPanel },
  { key: "backups", label: "Backups", icon: DatabaseBackup, component: BackupsTab },
  { key: "audit", label: "Audit log", icon: ScrollText, component: AuditLogTab },
];

const readHashTab = () => {
  if (typeof window === "undefined") return "review";
  const raw = window.location.hash.replace("#", "").toLowerCase();
  return TABS.some((t) => t.key === raw) ? raw : "review";
};

export default function AdminReview() {
  useNoIndex();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const { stats } = useAdminStats();
  const [active, setActive] = useState(readHashTab);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true, state: { from: "/admin" } });
    } else if (!user.is_admin) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const onHash = () => setActive(readHashTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const setTab = (key) => {
    setActive(key);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${key}`);
    }
  };

  if (authLoading || !user || !user.is_admin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-olive" />
      </main>
    );
  }

  const ActiveTab = TABS.find((t) => t.key === active)?.component ?? CampaignReviewTab;

  return (
    <main data-testid="admin-review-page" className="bg-sand">
      <Navbar
        onStart={async () => { await logout(); navigate("/"); }}
        primaryLabel="Sign out"
        secondaryLabel={null}
      />

      <section className="mx-auto max-w-6xl px-6 pb-8 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm"><PageAccent className="mb-5" /></FadeUp>
        <FadeUp>
          <span className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
            Admin console
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
            Everything waiting on <EmSwash>you.</EmSwash>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-olive">
            Five queues — Campaign Review, Student Verification, Wall Moderation, the weekly Disbursement batch, and a full Audit log of every action taken. Select as many rows as you like, then act on the whole batch in one click.
          </p>
        </FadeUp>

        {/* Stat strip */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="admin-stats">
            <StatChip label="Campaigns to review" value={stats.pending_review} testid="stat-pending-review" />
            <StatChip label="Students to verify" value={stats.pending_verification} testid="stat-pending-verification" />
            <StatChip label="Live campaigns" value={stats.live} testid="stat-live" />
            <StatChip label="Students total" value={stats.total_students} testid="stat-students" />
            <StatChip label="Donors total" value={stats.total_donors} testid="stat-donors" />
          </div>
        )}

        {/* Live-domain health probe — pings FRONTEND_ORIGIN so admins can
            confirm the public site is up without leaving the console. */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <DomainHealthWidget />
          </div>
          <div className="flex flex-col gap-1.5 sm:max-w-xs">
            <BackupDownloadButton />
            <BackupStatus />
          </div>
        </div>
        <div className="mt-4">
          <ConsentStatsWidget />
        </div>
        <div className="mt-4">
          <PaperLightDial />
        </div>
      </section>

      {/* Tabs */}
      <section className="mx-auto max-w-6xl px-6 lg:px-8" data-testid="admin-tabs">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border">
          <div className="flex flex-wrap gap-1">
            {TABS.map((t) => {
              const isActive = active === t.key;
              const badge = t.statKey && stats ? stats[t.statKey] : null;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  aria-selected={isActive}
                  role="tab"
                  className={`relative inline-flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                    isActive ? "text-ink" : "text-olive hover:text-ink"
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                  <span>{t.label}</span>
                  {typeof badge === "number" && badge > 0 && (
                    <span
                      data-testid={`admin-tab-${t.key}-badge`}
                      className={`ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                        isActive ? "bg-ink text-sand" : "bg-yellow text-ink"
                      }`}
                    >
                      {badge}
                    </span>
                  )}
                  {isActive && (
                    <motion.span
                      layoutId="admin-tab-underline"
                      className="absolute inset-x-0 -bottom-px h-0.5 bg-ink"
                      transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/founding")}
            data-testid="admin-founding-link"
            className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3.5 py-1.5 text-[11px] font-semibold text-ink transition hover:bg-beige"
          >
            <UserCog className="h-3.5 w-3.5" strokeWidth={2} />
            Founding donors →
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/team")}
            data-testid="admin-nav-team"
            className="mb-2 ml-2 inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-bone px-3.5 py-1.5 text-[11px] font-semibold text-ink transition hover:bg-beige"
          >
            <Camera className="h-3.5 w-3.5" strokeWidth={2} />
            Team page →
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-40 pt-8 lg:px-8 lg:pb-48">
        <ActiveTab />
      </section>

      <Footer
        onStart={async () => { await logout(); navigate("/"); }}
        onDonate={() => navigate("/dashboard")}
        primaryLabel="Sign out"
        secondaryLabel="Back to dashboard"
      />
    </main>
  );
}

const StatChip = ({ label, value, testid }) => (
  <div className="rounded-xl border border-border bg-bone p-4" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">{label}</p>
    <p className="mt-1 font-serif text-3xl text-ink">{value}</p>
  </div>
);
